// src/print.ts
var printers = {
  Document(node) {
    return printList(node.definitions, "\n\n");
  },
  OperationDefinition(node) {
    const op = node.operation;
    const name = node.name ? printNode(node.name) : "";
    const varDefs = node.variableDefinitions;
    const vars = varDefs?.length ? `(${printList(varDefs, ", ")})` : "";
    const sel = printNode(node.selectionSet);
    return `${op}${name ? ` ${name}` : ""}${vars} ${sel}`;
  },
  VariableDefinition(node) {
    const variable = printNode(node.variable);
    const type = printNode(node.type);
    const defaultValue = node.defaultValue ? ` = ${printNode(node.defaultValue)}` : "";
    return `${variable}: ${type}${defaultValue}`;
  },
  SelectionSet(node) {
    const selections = node.selections;
    return `{
${indent(printList(selections, "\n"))}
}`;
  },
  Field(node) {
    const alias = node.alias ? `${printNode(node.alias)}: ` : "";
    const name = printNode(node.name);
    const args = node.arguments;
    const argStr = args?.length ? `(${printList(args, ", ")})` : "";
    const sel = node.selectionSet ? ` ${printNode(node.selectionSet)}` : "";
    return `${alias}${name}${argStr}${sel}`;
  },
  Argument(node) {
    const name = printNode(node.name);
    const value = printNode(node.value);
    return `${name}: ${value}`;
  },
  Variable(node) {
    return `$${printNode(node.name)}`;
  },
  Name(node) {
    return node.value;
  },
  NamedType(node) {
    return printNode(node.name);
  },
  NonNullType(node) {
    return `${printNode(node.type)}!`;
  },
  ListType(node) {
    return `[${printNode(node.type)}]`;
  },
  ObjectValue(node) {
    const fields = node.fields;
    return `{${printList(fields, ", ")}}`;
  },
  ObjectField(node) {
    const name = printNode(node.name);
    const value = printNode(node.value);
    return `${name}: ${value}`;
  },
  StringValue(node) {
    const value = node.value;
    if (node.block) {
      return `"""
${value}
"""`;
    }
    return JSON.stringify(value);
  },
  IntValue(node) {
    return node.value;
  },
  FloatValue(node) {
    return node.value;
  },
  EnumValue(node) {
    return node.value;
  },
  BooleanValue(node) {
    return node.value ? "true" : "false";
  },
  NullValue() {
    return "null";
  },
  ListValue(node) {
    const values = node.values;
    return `[${printList(values, ", ")}]`;
  }
};
function printNode(node) {
  const printer = printers[node.kind];
  if (!printer) {
    throw new Error(`printDocument: unsupported AST node kind "${node.kind}"`);
  }
  return printer(node);
}
function printList(nodes, separator) {
  return nodes.map(printNode).join(separator);
}
function indent(str) {
  return str.split("\n").map((line) => line ? `  ${line}` : line).join("\n");
}
function printDocument(document) {
  return printNode(document);
}

// src/logger.ts
var noopLogger = {
  info: () => {
  },
  warn: () => {
  },
  error: () => {
  },
  debug: () => {
  }
};
var sdkLogger = noopLogger;
function setLogger(logger) {
  sdkLogger = logger;
}
function getLogger() {
  return sdkLogger;
}
function isPlainObject(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}
function withLogContext(logger, context) {
  const withContext = (data) => {
    if (data.length === 0) return [{ ...context }];
    const [first, ...rest] = data;
    if (isPlainObject(first)) return [{ ...context, ...first }, ...rest];
    return [{ ...context }, ...data];
  };
  return {
    info: (message, ...data) => logger.info(message, ...withContext(data)),
    warn: (message, ...data) => logger.warn(message, ...withContext(data)),
    error: (message, ...data) => logger.error(message, ...withContext(data)),
    debug: (message, ...data) => logger.debug(message, ...withContext(data))
  };
}
function resetLogger() {
  sdkLogger = noopLogger;
}

// src/errors.ts
var ClientSideExecutorError = class extends Error {
  constructor() {
    super(
      "Cannot create fallback executor: admin secret is not available. This typically occurs in client-side environments (e.g., React apps) where environment variables are not accessible. Please define your own executor and set it using the init() function to override the fallback executor. Example: init({ executor: yourCustomExecutor })"
    );
    this.name = "ClientSideExecutorError";
  }
};
var MissingGraphQLUrlError = class extends Error {
  constructor(_options) {
    const message = "Cannot create GraphQL executor: missing graphqlUrl. Set HASURA_GRAPHQL_URL (or GQL_URL) environment variable or pass url option to createFetchExecutor().";
    super(message);
    this.name = "MissingGraphQLUrlError";
  }
};
var UninitializedSDKError = class extends Error {
  constructor() {
    super(
      'SDK not initialized. Call init() with an executor configuration, or pass an executor explicitly. Example: init({ executor: createFetchExecutor({ url: "...", headers: { ... } }) })'
    );
    this.name = "UninitializedSDKError";
  }
};
var GraphQLHttpError = class extends Error {
  constructor(status, statusText, url) {
    super(
      `GraphQL request failed: ${status} ${statusText}` + (url ? ` (${url})` : "") + ". This may indicate a network issue, server error, or authentication problem."
    );
    this.status = status;
    this.statusText = statusText;
    this.url = url;
    this.name = "GraphQLHttpError";
  }
};
var GraphQLServerError = class extends Error {
  constructor(errors) {
    const messages = errors.map((e) => e.message).join(", ");
    super(
      `GraphQL server returned errors: ${messages}. This may indicate validation failures, authorization issues, or business logic errors.`
    );
    this.errors = errors;
    this.name = "GraphQLServerError";
  }
};
var GraphQLResponseParseError = class extends Error {
  constructor(originalError, url) {
    const errorMessage = originalError instanceof Error ? originalError.message : String(originalError);
    super(
      `Failed to parse GraphQL response as JSON: ${errorMessage}` + (url ? ` (${url})` : "") + ". The server may have returned an invalid response format."
    );
    this.originalError = originalError;
    this.url = url;
    this.name = "GraphQLResponseParseError";
    if (originalError instanceof Error) {
      this.cause = originalError;
    }
  }
};
var MissingConfigurationError = class extends Error {
  constructor(missingKeys) {
    super(
      `Missing required SDK configuration: ${missingKeys.join(", ")}. Set the corresponding environment variables or provide them via configuration.`
    );
    this.missingKeys = missingKeys;
    this.name = "MissingConfigurationError";
  }
};
var NetworkError = class extends Error {
  constructor(originalError, url) {
    const errorMessage = originalError instanceof Error ? originalError.message : String(originalError);
    super(
      `Network request failed: ${errorMessage}` + (url ? ` (${url})` : "") + ". This may indicate a connectivity issue, DNS resolution failure, or request timeout."
    );
    this.originalError = originalError;
    this.url = url;
    this.name = "NetworkError";
    if (originalError instanceof Error) {
      this.cause = originalError;
    }
  }
};

// src/executor.ts
var defaultExecutorOverride = null;
async function executeGraphQL(url, staticHeaders, document, variables, options) {
  const query = document.__text ?? printDocument(document);
  const mergedHeaders = {
    "Content-Type": "application/json",
    ...staticHeaders,
    ...options?.role ? { "x-hasura-role": options.role } : void 0,
    ...options?.headers
  };
  let response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: mergedHeaders,
      body: JSON.stringify({ query, variables }),
      signal: options?.signal
    });
  } catch (error) {
    throw new NetworkError(error, url);
  }
  if (!response.ok) {
    throw new GraphQLHttpError(response.status, response.statusText, url);
  }
  let result;
  try {
    result = await response.json();
  } catch (error) {
    throw new GraphQLResponseParseError(error, url);
  }
  if (result.errors && result.errors.length > 0) {
    throw new GraphQLServerError(result.errors);
  }
  return result.data;
}
function createFetchExecutor(config) {
  if (!config.url) {
    throw new MissingGraphQLUrlError();
  }
  const headers = {
    ...config.headers
  };
  if (config.adminSecret) {
    headers["x-hasura-admin-secret"] = config.adminSecret;
  }
  return {
    query: async (document, variables, options) => {
      return executeGraphQL(config.url, headers, document, variables, options);
    },
    mutate: async (document, variables, options) => {
      return executeGraphQL(config.url, headers, document, variables, options);
    }
  };
}
function init(options) {
  if (options.executor && options.executorConfig) {
    throw new Error('Provide either "executor" or "executorConfig", not both.');
  }
  if (!options.executor && !options.executorConfig) {
    throw new Error('Must provide either "executor" (pre-created) or "executorConfig" (to create one).');
  }
  defaultExecutorOverride = options.executor ?? createFetchExecutor(options.executorConfig);
  if (options.logger) {
    setLogger(options.logger);
  }
}
function resolveExecutor(executor) {
  if (executor) return executor;
  if (defaultExecutorOverride) return defaultExecutorOverride;
  throw new UninitializedSDKError();
}
function resetDefaultExecutor() {
  defaultExecutorOverride = null;
}

// src/geo.ts
var EARTH_RADIUS_METERS = 6371e3;
var METERS_PER_MILE = 1609.344;
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}
function calculateDistanceMeters(lat1, lon1, lat2, lon2) {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}
function metersToMiles(meters) {
  return meters / METERS_PER_MILE;
}
export {
  ClientSideExecutorError,
  GraphQLHttpError,
  GraphQLResponseParseError,
  GraphQLServerError,
  MissingConfigurationError,
  MissingGraphQLUrlError,
  NetworkError,
  UninitializedSDKError,
  calculateDistanceMeters,
  createFetchExecutor,
  getLogger,
  init,
  metersToMiles,
  printDocument,
  resetDefaultExecutor,
  resetLogger,
  resolveExecutor,
  withLogContext
};
//# sourceMappingURL=index.mjs.map